import React, { useContext } from 'react';
import styled from 'styled-components';
import { MdOutlineStar, MdOutlineStarBorder } from 'react-icons/md';
import SanityImage from 'gatsby-plugin-sanity-image';
import { LocaleContext } from '../contexts/LocaleContext';
import { SanityBlockBuilderTestimonialCarouselItem } from '../models/gatsby-graphql-types';
import getInternalLink from '../utils/getInternalLink';
import { LocalizedLink } from '@repo/gatsby-localization-helpers';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import sharedStyles from './styles';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${sharedStyles.spacing['spacing-lg']};
  padding-top: ${sharedStyles.spacing['spacing-2xl']};
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.searchBorder};
  margin-top: 60px;
  flex-grow: 1;
  box-sizing: border-box;
`;

const StarsContainer = styled.div`
  display: flex;
  margin-bottom: ${sharedStyles.spacing['spacing-md']};
`;

const FilledStar = styled(MdOutlineStar)`
  color: ${({ theme }) => theme.colors.brandPrimary};
  font-size: ${sharedStyles.fontSize['1xl']};
`;
const OutlineStar = styled(MdOutlineStarBorder)`
  color: ${({ theme }) => theme.colors.brandPrimary};
  font-size: ${sharedStyles.fontSize['1xl']};
`;

const Headline = styled.p`
  text-align: left;
  margin-bottom: ${sharedStyles.spacing['spacing-sm']};
  color: ${({ theme }) => theme.colors.defaultFont};
  font-size: ${sharedStyles.fontSize['2xl']};
  font-weight: ${sharedStyles.fontWeight.bold};
`;

const Quote = styled.q<{ headline?: string }>`
  &::before {
    content: ${props => (!props.headline ? "'\\201C'" : "''")};
    display: ${props => (!props.headline ? 'block' : 'none')};
    color: ${({ theme }) => theme.colors.brandPrimary};
    font-size: ${sharedStyles.fontSize['6xl']};
    font-weight: ${sharedStyles.fontWeight.bold};
    height: ${sharedStyles.spacing['spacing-2xl']};
  }

  flex-grow: 1;
  font-weight: ${sharedStyles.fontWeight.bold};
  line-height: 1.5;
`;

const Attribution = styled.div`
  text-align: right;
  margin-top: ${sharedStyles.spacing['spacing-lg']};
`;

const Author = styled.p`
  &:before {
    content: '\\2014 \\2003';
  }

  margin: 0;
`;

const Location = styled.p`
  margin: 0;
`;

const ImageContainer = styled.div`
  position: absolute;
  top: -55px;
  left: 50%;
  width: 110px;
  height: 110px;
  border-radius: 50%;
  overflow: hidden;
  transform: translate(-50%, 0);
  background: ${({ theme }) => theme.colors.cardBackground};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const LinkContainer = styled.div`
  text-align: center;
  margin-top: ${sharedStyles.spacing['spacing-lg']};
`;

function CarouselTestimonial({
  testimonialQuote,
  testimonialQuoteAuthor,
  testimonialLocation = '',
  testimonialHeadline = '',
  testimonialRating,
  testimonialImage,
  link,
}: Readonly<SanityBlockBuilderTestimonialCarouselItem>) {
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';

  const { tt } = useUiComponentsInternationalContext();

  const renderStars = starRating => {
    const stars = [];
    for (let i = 0; i < starRating; i += 1) {
      stars.push(
        <FilledStar aria-hidden key={`${testimonialLocation}-${i}`} />
      );
    }
    for (let i = starRating; stars.length < 5; i += 1) {
      stars.push(
        <OutlineStar aria-hidden key={`${testimonialLocation}-${i}`} />
      );
    }
    return stars;
  };
  const renderLink = () => {
    if (!link) {
      return null;
    }
    if (link.linkType === 'disable') {
      return link.caption ?? '';
    }
    if (link.linkType === 'internal' && link?.internalLink) {
      // Construct slug string. To remain typesafe, we must check that the properties exist since an internal link, may not have the respective property.
      let slug = '';

      if (
        'slug' in link.internalLink &&
        typeof link.internalLink.slug === 'object'
      ) {
        slug = `/${link.internalLink?.slug?.current}`;
      }

      if ('store' in link.internalLink) {
        slug = `/${link.internalLink?.store?.slug?.current}`;
      }

      return (
        <LinkContainer>
          <LocalizedLink
            key={`internal-${link.caption}`}
            to={getInternalLink({
              slug,
              currency,
            })}
          >
            {link.caption}
          </LocalizedLink>
        </LinkContainer>
      );
    }
    // Must be an external link at this point
    return (
      <LinkContainer>
        <a
          key={`external-${link.caption}`}
          href={link.href}
          target={link?.blank ? `_blank` : `_self`}
          rel="noreferrer"
        >
          {link.caption}
        </a>
      </LinkContainer>
    );
  };

  return (
    <Container>
      {testimonialImage?.asset && (
        <ImageContainer>
          <SanityImage
            width={110}
            asset={testimonialImage.asset}
            {...testimonialImage}
            alt={
              testimonialImage?.alt ||
              testimonialHeadline ||
              tt(s => s.carouselTestimonialComponent.testimonialAlt)
            }
          />
        </ImageContainer>
      )}
      {!!testimonialRating && (
        <StarsContainer
          role="img"
          aria-label={tt(
            s => s.carouselTestimonialComponent.testimonialStarsContainerAlt,
            { rating: testimonialRating.toString() }
          )}
        >
          {renderStars(testimonialRating)}
        </StarsContainer>
      )}
      {testimonialHeadline && <Headline>{testimonialHeadline}</Headline>}
      <Quote headline={testimonialHeadline}>{testimonialQuote}</Quote>
      <Attribution>
        <Author>{testimonialQuoteAuthor}</Author>
        {testimonialLocation && <Location>{testimonialLocation}</Location>}
      </Attribution>
      {renderLink()}
    </Container>
  );
}

export default CarouselTestimonial;
